import type { FieldSelection, ResolvedMonospaceCollection } from '#layers/data-model';
import type { JavascriptSerializable } from './js-serialization';
import type { CollectionCodeDataItem, CollectionCodeFieldObject, CollectionCodeFields, CollectionCodeSnippetOptions } from './types';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';

/**
 * Returns the default scalar field selection used when the caller has not selected fields.
 */
export function getDefaultCollectionCodeFields(collection: ResolvedMonospaceCollection): string[] {
	return getCollectionFields(collection, {
		includeRelationFields: false,
		includeUnsupportedFields: false,
	}).map((field) => field.apiName);
}

/**
 * Resolves explicit field input or falls back to the collection default scalar fields.
 */
export function resolveCodeFields(options: CollectionCodeSnippetOptions): FieldSelection {
	return normalizeCodeFields(options.fields) ?? getDefaultCollectionCodeFields(options.collection);
}

/**
 * Normalizes UI field objects and field selections to a FieldSelection.
 */
export function normalizeCodeFields(fields: CollectionCodeFields | undefined): FieldSelection | undefined {
	if (!fields?.length) {
		return;
	}

	return fields.map((field) => isCollectionCodeFieldObject(field) ? field.apiName : field) as FieldSelection;
}

/**
 * Converts a field selection into values supported by the snippet serializer.
 */
export function toSerializableFieldSelection(fields: FieldSelection): JavascriptSerializable[] {
	return fields.map((field) => toJavascriptSerializable(field));
}

/**
 * Coerces unknown nested field-selection values into serializer-safe values.
 */
export function toJavascriptSerializable(value: unknown): JavascriptSerializable {
	if (Array.isArray(value)) {
		return value.map((item) => toJavascriptSerializable(item));
	}

	if (value && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value).map(([key, nestedValue]) => [key, toJavascriptSerializable(nestedValue)]),
		) as CollectionCodeDataItem;
	}

	if (
		typeof value === 'string'
		|| typeof value === 'number'
		|| typeof value === 'boolean'
		|| value === null
		|| value === undefined
	) {
		return value;
	}

	return String(value);
}

/**
 * Checks whether a field selection entry is a resolved collection field object.
 */
function isCollectionCodeFieldObject(value: unknown): value is CollectionCodeFieldObject {
	return typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
		&& 'apiName' in value
		&& typeof value.apiName === 'string';
}
